import type { PlayerCharacter } from "../types/entity.js";
import { abilityModifier } from "../types/entity.js";
import { rollD20 } from "../combat/dice.js";

/** Item id of a set of Thieves' Tools (matches `public/data/items/thieves-tools.json`). */
export const THIEVES_TOOLS_ITEM_ID = "thieves-tools";

/**
 * I17 — pure lockpicking resolver.
 *
 * Thieves' Tools are a consumable: a successful check keeps them, a
 * failed check breaks them. The caller is responsible for removing
 * the tool stack from inventory on `toolsBroken`. The roll is
 * `d20 + DEX mod + proficiency bonus` — we assume a party member
 * wielding thieves' tools is trained enough to add proficiency, since
 * the MVP party type (`PlayerCharacter`) does not yet track tool
 * proficiency separately (that arrives with C01's full `Character`
 * promotion).
 */

export type LockpickOutcome = "no_tools" | "success" | "failure";

export interface LockpickInput {
  readonly picker: PlayerCharacter;
  readonly lockDc: number;
  readonly hasTools: boolean;
}

export interface LockpickResult {
  readonly outcome: LockpickOutcome;
  readonly roll: number;
  readonly bonus: number;
  readonly total: number;
  readonly toolsBroken: boolean;
}

export function attemptLockpick(input: LockpickInput): LockpickResult {
  if (!input.hasTools) {
    return { outcome: "no_tools", roll: 0, bonus: 0, total: 0, toolsBroken: false };
  }
  const bonus = abilityModifier(input.picker.abilities.DEX) + input.picker.proficiencyBonus;
  const roll = rollD20();
  const total = roll + bonus;
  const success = total >= input.lockDc;
  return {
    outcome: success ? "success" : "failure",
    roll,
    bonus,
    total,
    toolsBroken: !success,
  };
}
